import React, { useLayoutEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";

// Bootstrap CSS is now loaded from CDN in index.html for better caching
import "./style/index.css";
import { Button } from "@aws-amplify/ui-react";
import { fetchAuthSession } from "aws-amplify/auth";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import DEBUG_CONFIG from "./config/env.local";

import { lazy, Suspense } from "react";
import Navbars from "./components/Nav";
import { config } from "./pages/Test/config";

// 使用者閒置設定（以分鐘為單位設定閒置時間）
const IDLE_CONFIG = {
  inactivityMinutes: 30, // 30 分鐘無操作後顯示倒數提示
  warningSeconds: 10, // 顯示 10 秒倒數，時間到自動登出
};

// Lazy load components for code splitting
const Test = lazy(() => import("./pages/Test/Test"));
const Managerment = lazy(() => import("./pages/Test/Management"));
const ContactMe = lazy(() => import("./pages/ContactMe/ContactMe"));
const Onsite = lazy(() => import("./pages/Test/Onsite"));
const AuthComponent = lazy(() => import("./pages/Login/Auth"));
const Report = lazy(() => import("./pages/Test/Report"));
const Tokens = lazy(() => import("./pages/Test/Tokens"));

// import LoginComponent from "./pages/Login/login-component";

const isReportOnlyUser = (user) => user?.account === "admin1";

const getDefaultAuthorizedPath = (user) =>
  isReportOnlyUser(user) ? "/report" : "/management";

// 受保護的路由組件
const ProtectedRoute = ({
  user,
  isLoading,
  children,
  fallback = "/",
  allow = () => true,
}) => {
  console.log("ProtectedRoute: 檢查用戶狀態:", user);
  console.log("ProtectedRoute: 當前路徑:", window.location.pathname);

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
          color: "#666",
        }}
      >
        載入中...
      </div>
    );
  }

  if (!user) {
    console.log("ProtectedRoute: 用戶未登入，重定向到:", fallback);
    console.log("ProtectedRoute: 重定向原因: user 為 null 或 undefined");
    return <Navigate to={fallback} replace />;
  }

  if (!allow(user)) {
    const redirectPath = getDefaultAuthorizedPath(user);
    console.log("ProtectedRoute: 用戶無權限，重定向到:", redirectPath);
    return <Navigate to={redirectPath} replace />;
  }

  console.log("ProtectedRoute: 用戶已登入，渲染受保護的內容");
  console.log("ProtectedRoute: 用戶信息:", user);
  return children;
};

const EntryPage = () => {
  const navigate = useNavigate();

  return (
    <div
      className="entry-page"
      style={{
        width: "100%",
        minHeight: "100vh",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        gap: "20px",
        boxSizing: "border-box",
      }}
    >
      <h1 style={{ textAlign: "center", fontSize: "clamp(20px, 6vw, 32px)" }}>
        兒童成長發育門診系統
      </h1>
      <h2 style={{ color: "gray", textAlign: "center" }}>請選擇診所</h2>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "16px",
          width: "100%",
          maxWidth: "520px",
        }}
      >
        <Button
          onClick={() => navigate("/hansheng")}
          style={{
            backgroundColor: "#A8D8B9",
            border: "none",
            color: "black",
            padding: "10px 20px",
            fontSize: "1.6em",
            flex: "1 1 180px",
            minHeight: "120px",
          }}
        >
          <>
            <div style={{ textAlign: "center" }}>
              菡生
              <br />
              婦幼診所
              <span
                style={{ color: "gray", display: "block", fontSize: "0.8em" }}
              >
                板橋
              </span>
            </div>
          </>
        </Button>
        <Button
          onClick={() => navigate("/yoyo")}
          style={{
            backgroundColor: "#F4A7B9",
            border: "none",
            color: "black",
            padding: "10px 20px",
            fontSize: "1.6em",
            flex: "1 1 180px",
            minHeight: "120px",
          }}
        >
          <>
            <div style={{ textAlign: "center" }}>
              宥宥
              <br />
              婦幼診所
              <span
                style={{ color: "gray", display: "block", fontSize: "0.8em" }}
              >
                蘆洲
              </span>
            </div>
          </>
        </Button>
      </div>
    </div>
  );
};

// AppContent 組件 - 使用 useAuth hook
const AppContent = () => {
  const {
    user,
    clinicName,
    isLoading,
    signIn,
    signOut,
    setTestUser,
    clearUserState,
  } = useAuth();

  const [width, setWidth] = React.useState(window.innerWidth);
  const [height, setHeight] = React.useState(window.innerHeight);
  const [idleWarningVisible, setIdleWarningVisible] = React.useState(false);
  const [idleCountdown, setIdleCountdown] = React.useState(
    IDLE_CONFIG.warningSeconds,
  );
  const inactivityTimeoutRef = React.useRef(null);
  const warningTimeoutRef = React.useRef(null);
  const warningIntervalRef = React.useRef(null);
  const warningActiveRef = React.useRef(false);

  useLayoutEffect(() => {
    // 这个函数会在每次渲染后立即同步调用
    window.addEventListener("error", (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div",
        );
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay",
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute("style", "display: none");
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute("style", "display: none");
        }
      }
    });
  }, []);

  React.useEffect(() => {
    function handleResize() {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const clearAllIdleTimers = React.useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current);
      warningIntervalRef.current = null;
    }
  }, []);

  const startInactivityTimer = React.useCallback(() => {
    clearAllIdleTimers();
    warningActiveRef.current = false;
    setIdleWarningVisible(false);
    inactivityTimeoutRef.current = setTimeout(
      () => {
        warningActiveRef.current = true;
        setIdleWarningVisible(true);
        let remaining = IDLE_CONFIG.warningSeconds;
        setIdleCountdown(remaining);
        warningIntervalRef.current = setInterval(() => {
          remaining -= 1;
          setIdleCountdown(remaining);
        }, 1000);

        warningTimeoutRef.current = setTimeout(async () => {
          if (!warningActiveRef.current) return;
          try {
            setIdleWarningVisible(false);
            if (warningIntervalRef.current) {
              clearInterval(warningIntervalRef.current);
              warningIntervalRef.current = null;
            }
            await signOut();
          } catch (e) {
            console.error("自動登出失敗:", e);
          }
        }, IDLE_CONFIG.warningSeconds * 1000);
      },
      IDLE_CONFIG.inactivityMinutes * 60 * 1000,
    );
  }, [clearAllIdleTimers, signOut]);

  const resetIdleTimer = React.useCallback(() => {
    if (!user) return;
    startInactivityTimer();
  }, [user, startInactivityTimer]);

  React.useEffect(() => {
    if (!user) {
      clearAllIdleTimers();
      setIdleWarningVisible(false);
      return;
    }

    startInactivityTimer();

    const activityHandler = () => {
      if (warningActiveRef.current) {
        warningActiveRef.current = false;
        setIdleWarningVisible(false);
      }
      resetIdleTimer();
    };

    const events = [
      "mousemove",
      "keydown",
      "click",
      "touchstart",
      "scroll",
      "wheel",
    ];
    events.forEach((evt) =>
      window.addEventListener(evt, activityHandler, { passive: true }),
    );

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, activityHandler));
      clearAllIdleTimers();
    };
  }, [user, resetIdleTimer, startInactivityTimer, clearAllIdleTimers]);

  return (
    <div>
      {/* 調試信息 - 使用本地配置控制 */}
      {DEBUG_CONFIG.isDebugEnabled() && (
        <div
          style={{
            position: "fixed",
            bottom: "10px",
            left: "10px",
            backgroundColor: "rgba(0,0,0,0.8)",
            color: "white",
            padding: "10px",
            borderRadius: "5px",
            fontSize: "12px",
            zIndex: 1000,
            maxWidth: "350px",
            textAlign: "left",
          }}
        >
          <strong>調試信息:</strong>
          <br />
          用戶狀態: {user ? "已登入" : "未登入"}
          <br />
          用戶數據: {user ? JSON.stringify(user, null, 2) : "null"}
          <br />
          診所名稱: {clinicName ? clinicName : "未設置"}
          <br />
          當前路徑: {window.location.pathname}
          <br />
          Context 用戶狀態: {user ? "已登入" : "未登入"}
          <br />
          <br />
          <Button
            size="small"
            onClick={async () => {
              try {
                const authSession = await fetchAuthSession();
                if (authSession.tokens && authSession.tokens.accessToken) {
                  const accessToken = authSession.tokens.accessToken;
                  const tokenExpiry = new Date(accessToken.payload.exp * 1000);
                  console.log("Cognito Token 狀態:", {
                    exists: true,
                    expiry: tokenExpiry,
                    isValid: Date.now() < accessToken.payload.exp * 1000,
                  });
                  alert(`Token 過期時間: ${tokenExpiry.toLocaleString()}`);
                } else {
                  console.log("沒有 Cognito Token");
                  alert("沒有 Cognito Token");
                }
              } catch (error) {
                console.error("檢查 Token 失敗:", error);
                alert("檢查 Token 失敗");
              }
            }}
            style={{ fontSize: "10px", padding: "2px 6px" }}
          >
            檢查 Cognito Token
          </Button>
          <br />
          <Button
            size="small"
            onClick={() => {
              const testUserData = {
                account: "admin",
                email: "admin@example.com",
                clinic: "all",
                createTime: config.getUTC8Time().toISOString(),
              };
              setTestUser(testUserData);
              console.log("手動設置測試用戶完成:", testUserData);
            }}
            style={{ fontSize: "10px", padding: "2px 6px", marginTop: "5px" }}
          >
            設置測試用戶
          </Button>
          <br />
          <Button
            size="small"
            onClick={() => {
              clearUserState();
              console.log("清除用戶狀態完成");
            }}
            style={{ fontSize: "10px", padding: "2px 6px", marginTop: "5px" }}
          >
            清除用戶狀態
          </Button>
        </div>
      )}
      {idleWarningVisible && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              color: "#333",
              padding: "20px 24px",
              borderRadius: "8px",
              width: "min(92vw, 480px)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              textAlign: "center",
            }}
          >
            <h3 style={{ margin: "0 0 8px 0" }}>您已閒置</h3>
            <p style={{ margin: "0 0 16px 0", lineHeight: 1.6 }}>
              將在 {idleCountdown} 秒後自動登出。
              若要繼續使用，請移動滑鼠、按鍵或點擊任意處。
            </p>
            <Button
              onClick={resetIdleTimer}
              style={{
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                padding: "8px 14px",
                fontSize: "14px",
              }}
            >
              繼續使用
            </Button>
          </div>
        </div>
      )}
      {user && <Navbars />}
      <Suspense
        fallback={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
              fontSize: "18px",
              color: "#666",
            }}
          >
            載入中...
          </div>
        }
      >
        <Routes>
          <Route
            path="/EntryPage"
            element={
              user && isReportOnlyUser(user) ? (
                <Navigate to="/report" replace />
              ) : (
                <EntryPage />
              )
            }
          />

          <Route
            path="/hansheng"
            element={
              user && isReportOnlyUser(user) ? (
                <Navigate to="/report" replace />
              ) : (
                <Test user={user} />
              )
            }
          />
          <Route
            path="/yoyo"
            element={
              user && isReportOnlyUser(user) ? (
                <Navigate to="/report" replace />
              ) : (
                <Test user={user} />
              )
            }
          />
          <Route
            path="/onsite"
            element={
              user && isReportOnlyUser(user) ? (
                <Navigate to="/report" replace />
              ) : (
                <Onsite />
              )
            }
          />
          <Route
            path="/Contact_Me"
            element={
              <ProtectedRoute
                user={user}
                isLoading={isLoading}
                fallback="/"
                allow={(currentUser) => !isReportOnlyUser(currentUser)}
              >
                <ContactMe />
              </ProtectedRoute>
            }
          />
          <Route
            path="/management"
            element={
              <ProtectedRoute
                user={user}
                isLoading={isLoading}
                fallback="/"
                allow={(currentUser) => !isReportOnlyUser(currentUser)}
              >
                <Managerment user={user} clinicName={clinicName} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/report"
            element={
              <ProtectedRoute
                user={user}
                isLoading={isLoading}
                fallback="/"
                allow={(currentUser) =>
                  ["admin", "admin1"].includes(currentUser?.account)
                }
              >
                <Report user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tokens"
            element={
              <ProtectedRoute
                user={user}
                isLoading={isLoading}
                fallback="/"
                allow={(currentUser) =>
                  currentUser?.account === "dennis" &&
                  !isReportOnlyUser(currentUser)
                }
              >
                <Tokens />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <div
                style={{
                  width: "100%",
                  height: "100vh",
                  margin: "0 auto",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {console.log("=== App.js 根路由渲染 ===")}
                {console.log("signIn 函數:", signIn)}
                {console.log("準備渲染 AuthComponent...")}
                <AuthComponent signIn={signIn} />
                {console.log("AuthComponent 渲染完成")}
              </div>
            }
          />
        </Routes>
      </Suspense>
    </div>
  );
};

// 主要的 App 組件 - 提供 AuthProvider
const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
